// Shared phone country data for all PhoneInput components.
// Overrides only Argentina (AR) and Mexico (MX) masks — all other countries
// keep their library-default formatting.
//
// AR fix: The library's default mask `(..) ........` provides only 10 digit
// slots, but Argentine mobile numbers need 11 digits after +54 (the `9` mobile
// prefix + 10 national digits). This caused silent truncation blocking sign-up.
// The override mask `.. .... .....` provides exactly 11 slots (2+4+5),
// covering both landline (10 digits) and mobile (11 digits).
//
// MX override: Preserves the existing `(..) .... ....` format used across
// the app for Mexican numbers.
//
// FLAG_IMAGES: Overrides the default flag CDN (cdnjs.cloudflare.com/twemoji)
// with a proxy route on the nginx-api-gateway (/resources/images/countries/).
// This keeps the CSP img-src pointed at *.leadsales.services only.
// Pass this to every <CountrySelector> and <PhoneInput> via the `flags` prop.
import {
  defaultCountries,
  parseCountry,
  buildCountryData,
} from 'react-international-phone';
import type { CountryData, CustomFlagImage } from 'react-international-phone';

const API_GATEWAY_BASE = (import.meta.env.VITE_BASE_URL_LEGACY ?? '').replace(/\/$/, '').replace(/\/v2\/web.*$/, '') as string;
const TWEMOJI_BASE_URL = `${API_GATEWAY_BASE}/resources/images/countries`;

const isoToTwemojiFilename = (iso2: string): string => {
  return iso2
    .toUpperCase()
    .split('')
    .map((char) => (0x1f1e6 + char.charCodeAt(0) - 65).toString(16))
    .join('-');
};

export const FLAG_IMAGES: CustomFlagImage[] = defaultCountries.map((country) => {
  const { iso2 } = parseCountry(country);
  return { iso2, src: `${TWEMOJI_BASE_URL}/${isoToTwemojiFilename(iso2)}.svg` };
});

export const PHONE_COUNTRIES: CountryData[] = defaultCountries.map(
  (country) => {
    const parsed = parseCountry(country);

    if (parsed.iso2 === 'mx') {
      return buildCountryData({ ...parsed, format: '(..) .... ....' });
    }

    if (parsed.iso2 === 'ar') {
      return buildCountryData({ ...parsed, format: '.. .... .....' });
    }

    return country;
  },
);
